import { AlertTriangle, Package, PackageCheck, Truck, XCircle } from 'lucide-react'
import type { ShipmentStatus } from '@/types'
import { SHIPMENT_STATUS_LABELS } from '@/lib/shipment'
import { cn } from '@/lib/cn'
import Badge from '@/components/ui/Badge'

/**
 * Where a parcel is, in one chip. Two renderings, the UrgentBadge pattern:
 * `portal` is the tinted Icewall chip for the consoles; `storefront` is a
 * neutral outline pill with the colour on the text only, because the tinted
 * recipe is banned from the storefront surface.
 */
export default function ShipmentStatusBadge({
  status,
  variant = 'portal',
  className,
}: {
  status: ShipmentStatus
  variant?: 'portal' | 'storefront'
  className?: string
}) {
  const label = SHIPMENT_STATUS_LABELS[status] ?? status
  const Icon = ICONS[status] ?? Package

  if (variant === 'storefront') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-foreground/15 px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide',
          STOREFRONT_TEXT[status] ?? 'text-foreground/70',
          className,
        )}
      >
        <Icon className="size-3" />
        {label}
      </span>
    )
  }

  return (
    <Badge tone={PORTAL_TONE[status] ?? 'neutral'} className={cn(status === 'cancelled' && 'opacity-60', className)}>
      <Icon className="size-3" />
      {label}
    </Badge>
  )
}

const ICONS: Partial<Record<ShipmentStatus, typeof Package>> = {
  booked: Package,
  picked_up: Truck,
  in_transit: Truck,
  out_for_delivery: Truck,
  delivered: PackageCheck,
  failed: AlertTriangle,
  returning: AlertTriangle,
  returned: AlertTriangle,
  exception: AlertTriangle,
  cancelled: XCircle,
}

const PORTAL_TONE: Record<ShipmentStatus, 'neutral' | 'brand' | 'success' | 'warning' | 'danger'> = {
  booked: 'neutral',
  picked_up: 'brand',
  in_transit: 'brand',
  out_for_delivery: 'warning',
  delivered: 'success',
  failed: 'danger',
  returning: 'danger',
  returned: 'danger',
  exception: 'danger',
  cancelled: 'neutral',
}

const STOREFRONT_TEXT: Record<ShipmentStatus, string> = {
  booked: 'text-foreground/70',
  picked_up: 'text-primary',
  in_transit: 'text-primary',
  out_for_delivery: 'text-warning',
  delivered: 'text-success',
  failed: 'text-danger',
  returning: 'text-danger',
  returned: 'text-danger',
  exception: 'text-danger',
  cancelled: 'text-foreground/50',
}
